import type {
  ExportRunStatus,
  ExportRunView,
  ImportMode,
  ImportRunKind,
  ImportRunStatus,
} from '../../data/client';

/**
 * EVERY STATE THIS MODULE CAN BE IN, AS A SENTENCE.
 *
 * The workflow module's convention, applied to a second job system: a status enum that
 * reaches a screen is how somebody ends up reading `blocked_recursion`, and here it would
 * be `skipped_touched` - a word that means something precise and nothing obvious. So each
 * status has a LABEL (two or three words, for a badge) and a SENTENCE (what it means for
 * the person reading it, in full).
 *
 * The sentences are deliberately not symmetrical between the two kinds of run. `completed`
 * on an import means the job finished and NOT that every row landed - the counters say the
 * rest, which is why its sentence points at them rather than declaring success.
 */

export const IMPORT_RUN_STATUS_LABELS: Readonly<Record<ImportRunStatus, string>> = {
  draft: 'Not set up yet',
  validating: 'Checking',
  ready: 'Ready to run',
  queued: 'Waiting to start',
  running: 'Running',
  completed: 'Finished',
  failed: 'Failed',
  canceled: 'Stopped',
};

const IMPORT_SENTENCES: Readonly<Record<ImportRunStatus, string>> = {
  draft: 'Nobody has finished setting this import up. Open it to choose the columns and the mode.',
  validating: 'The columns and the values are being checked. Nothing has been written yet.',
  ready: 'The columns are mapped and the numbers have been checked. Nothing has been written yet.',
  queued: 'This import is waiting to start. Nothing has been written yet.',
  running: 'Rows are being written now. The counts below update every couple of seconds.',
  completed: 'This import finished. The counts say what happened to each row.',
  failed:
    'This import stopped partway through. The rows written before it stopped are still there.',
  canceled: 'Somebody stopped this import. The rows written before it stopped are still there.',
};

const REVERT_SENTENCES: Readonly<Record<ImportRunStatus, string>> = {
  draft: 'This undo has not started.',
  validating: 'This undo has not started.',
  ready: 'This undo has not started.',
  queued: 'This undo is waiting to start. Nothing has been deleted yet.',
  running: 'Records are being deleted now. The counts below update every couple of seconds.',
  completed: 'This undo finished. The report lists every record it considered.',
  failed: 'This undo stopped partway through. Records deleted before it stopped stay deleted.',
  canceled: 'Somebody stopped this undo. Records deleted before it stopped stay deleted.',
};

export function importRunSentence(kind: ImportRunKind, status: ImportRunStatus): string {
  return kind === 'revert' ? REVERT_SENTENCES[status] : IMPORT_SENTENCES[status];
}

export const EXPORT_RUN_STATUS_LABELS: Readonly<Record<ExportRunStatus, string>> = {
  queued: 'Waiting to start',
  running: 'Running',
  completed: 'Ready to download',
  failed: 'Failed',
};

/**
 * EXPIRED IS NOT A STATUS AND IS SHOWN LIKE ONE.
 *
 * D34 keeps the file for seven days and then nulls the bytes; the run row survives as the
 * audit record (D38), still reading `completed`. A screen that only rendered the status
 * would offer a Download button for a file that is gone, so the expiry is folded in here -
 * one function, so the badge and the sentence cannot disagree.
 */
export function isExpiredExport(run: ExportRunView): boolean {
  return run.status === 'completed' && !run.hasFile;
}

export function exportRunLabel(run: ExportRunView): string {
  return isExpiredExport(run) ? 'Expired' : EXPORT_RUN_STATUS_LABELS[run.status];
}

export function exportRunSentence(run: ExportRunView): string {
  if (isExpiredExport(run)) {
    return 'The file was kept for 7 days and has been deleted. Request the export again to get a new one.';
  }
  switch (run.status) {
    case 'queued':
      return 'This export is waiting for a worker to pick it up.';
    case 'running':
      return 'The file is being written now. It appears here when it is done.';
    case 'completed':
      return 'The file is ready. It stays available for 7 days.';
    case 'failed':
      return run.errorReason ?? 'This export stopped and produced no file.';
  }
}

/**
 * D15's three modes, in the words that say what they DO rather than what they are called.
 * The sentence is what the wizard renders under the choice, because "upsert" is a word
 * from a database and not from anybody's job.
 */
export const IMPORT_MODE_LABELS: Readonly<Record<ImportMode, string>> = {
  create: 'Add only',
  update: 'Update only',
  upsert: 'Add and update',
};

export const IMPORT_MODE_SENTENCES: Readonly<Record<ImportMode, string>> = {
  create:
    'Rows create new records. A mapped identifier that matches a deleted record is refused. Importing the file again can create duplicates.',
  update:
    'Rows that match an existing record update it. Rows that match nothing are skipped, so nothing new can appear.',
  upsert:
    'Rows that match an existing record update it, and rows that match nothing become new records.',
};

/**
 * D12's row errors, one sentence each. These are the codes the preview counts by, so the
 * confirm screen can say WHICH refusal dominates instead of "42 errors" - which is the
 * difference between a file somebody can fix and a number somebody ignores.
 */
export const ROW_ERROR_SENTENCES: Readonly<Record<string, string>> = {
  NO_MATCH_KEY_VALUE: 'the cell that says which record the row is, is empty',
  MULTI_MATCH: 'the value matches more than one record, and a row is never split across two',
  IN_FILE_DUPLICATE: 'two rows in the file carry the same key, so neither one can be trusted',
  MATCHES_DELETED_RECORD:
    'the value belongs to a record that was deleted, and an import never brings one back',
  STALE_IDENTITY_MATCH: 'the value only matches a record through a value that record no longer has',
  IDENTITY_UNAVAILABLE:
    'an identifier on this object is still being indexed, so no match can be trusted yet',
  ALL_VALUES_INVALID: 'every value in the row was empty or could not be read',
  WRITE_REJECTED: 'the row could not be saved; check your permissions and the values in the file',
};

export function rowErrorSentence(code: string): string {
  return ROW_ERROR_SENTENCES[code] ?? 'the row was refused for a reason this screen does not know';
}

/**
 * `a`, `a or b`, `a, b or c` - an English list of alternatives, because "a, b, c" reads like
 * a machine wrote it. `joinWords` in the admin module is the same idea with "and", which is
 * the wrong conjunction for a list of columns any one of which would have been enough.
 */
export function joinAlternatives(words: readonly string[]): string {
  if (words.length === 0) return '';
  if (words.length === 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} or ${words.at(-1) ?? ''}`;
}

/** "1 record" / "2 records", because a count without its noun reads as a code. */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : (pluralForm ?? `${singular}s`)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
